import React, { useContext, useMemo } from "react"

import { noop } from "shared/lib/noop"
import { ExternalDimensioningConfigurationMismatchError } from "shared/types/ExternalDimensioning"
import { ProcessStep } from "shared/types/ProcessStep"
import { ProductConfiguratorStepType } from "shared/types/ProductConfiguration"

import { ProductConfiguratorProps } from "../ProductConfigurator"
import { useProductConfiguratorSteps } from "./useProductConfiguratorSteps"

export interface ProductConfiguratorContextType {
  steps: ProcessStep<ProductConfiguratorStepType>[]

  activeStep: ProductConfiguratorStepType
  firstStep: ProductConfiguratorStepType
  activateStep: (stepType: ProductConfiguratorStepType) => void
  activatePreviousStep: () => void
  activateNextStep: () => void
  isPreviousStepDisabled: boolean
  isNextStepDisabled: boolean
  resetExternalDimensioning: () => void
  externalDimensioningConfigurationMismatchError?:
    | ExternalDimensioningConfigurationMismatchError
    | null
    | undefined
}

export const DEFAULT_PRODUCT_CONFIGURATOR_CONTEXT: ProductConfiguratorContextType =
  {
    steps: [],
    activeStep: ProductConfiguratorStepType.CONFIGURATION,
    firstStep: ProductConfiguratorStepType.CONFIGURATION,
    activateStep: noop,
    activatePreviousStep: noop,
    activateNextStep: noop,
    isPreviousStepDisabled: false,
    isNextStepDisabled: false,
    resetExternalDimensioning: noop
  }

export const ProductConfiguratorContext =
  React.createContext<ProductConfiguratorContextType | null>(null)

export function useProductConfiguratorContext(): ProductConfiguratorContextType {
  return (
    useContext(ProductConfiguratorContext) ??
    DEFAULT_PRODUCT_CONFIGURATOR_CONTEXT
  )
}

export function useProductConfiguratorContextValue(
  model: ProductConfiguratorProps
): ProductConfiguratorContextType {
  const {
    steps,
    isNextStepDisabled,
    isPreviousStepDisabled,
    activeStep,
    firstStep,
    activateStep,
    activateNextStep,
    activatePreviousStep,
    resetExternalDimensioning
  } = useProductConfiguratorSteps(model)
  const { externalDimensioningConfigurationMismatchError } = model

  return useMemo<ProductConfiguratorContextType>(
    () => ({
      steps,
      isNextStepDisabled,
      isPreviousStepDisabled,
      activeStep,
      firstStep,
      activateStep,
      activateNextStep,
      activatePreviousStep,
      resetExternalDimensioning,
      externalDimensioningConfigurationMismatchError
    }),
    [
      steps,
      isNextStepDisabled,
      isPreviousStepDisabled,
      activeStep,
      firstStep,
      activateStep,
      activateNextStep,
      activatePreviousStep,
      resetExternalDimensioning,
      externalDimensioningConfigurationMismatchError
    ]
  )
}

export function ProductConfiguratorContextProvider({
  children,
  model
}: {
  children: React.ReactNode | React.ReactNode[]
  model: ProductConfiguratorProps
}) {
  const value = useProductConfiguratorContextValue(model)

  return (
    <ProductConfiguratorContext.Provider value={value}>
      {children}
    </ProductConfiguratorContext.Provider>
  )
}
